/**
 * PortalAuthContext — estado de autenticação do paciente no `/portal`.
 *
 * Contexto: Firebase Auth tem uma única instância global (`auth.currentUser`)
 * compartilhada entre o staff e o portal. Os dois lados se distinguem pelas
 * custom claims: staff tem `role` em {ADMIN, MEDICO, ...}, paciente tem
 * `role === 'PACIENTE'`. Este provider só "vê" o usuário como logado quando
 * ele é efetivamente paciente — o staff AuthContext espelha o check inverso
 * (ignora provisioning quando role=PACIENTE).
 *
 * Por quê um provider separado em vez de extender o AuthContext:
 *   - O AuthContext existente puxa dados de `profile` (clinicId do staff,
 *     permissions, sessionTimeout da clínica) que não fazem sentido pra
 *     paciente. Misturar os dois criaria branches `if (role===PACIENTE)`
 *     em toda a árvore de componentes do staff.
 *   - Permite que o provider do portal fique isolado nas rotas `/portal/**`
 *     (ver `App.tsx`).
 *
 * O signup é feito via backend (`PortalAPI.signup`) porque precisa setar
 * custom claims e criar o doc em `portalUsers`. O LOGIN é client-side puro
 * (`signInWithEmailAndPassword`) — o backend `/auth/login` antigo não
 * validava senha e foi removido no Sprint 4.3.
 */
import { createContext, useEffect, useState, type ReactNode } from 'react';
import {
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { auth } from '@/services/firebase/config';
import { PortalAPI, PortalApiError } from '@/services/portal/client';

interface PortalUserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  clinicId: string;
}

interface PortalAuthContextType {
  user: User | null;
  profile: PortalUserProfile | null;
  loading: boolean;
  isPortalAuthenticated: boolean;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  loginWithGoogle: (clinicId: string) => Promise<void>;
  /** Loga com o custom token devolvido por `PortalAPI.whatsappVerify`. As
   *  claims PACIENTE já foram setadas no backend antes de emitir o token,
   *  então o `onAuthStateChanged` reconhece o paciente sem passo extra. */
  loginWithCustomToken: (token: string) => Promise<void>;
  /** "Esqueci a senha": manda o email de redefinição do próprio Firebase.
   *  Resolve sem erro também quando o email não tem conta — ver o corpo. */
  requestPasswordReset: (email: string) => Promise<void>;
  /** Erro vindo do RETORNO do signInWithRedirect (fallback quando o popup é
   *  bloqueado). Nesse fluxo a página remonta do zero e o erro não tem como
   *  chegar pelo throw do loginWithGoogle — as páginas de login/signup exibem
   *  este campo junto do erro local delas. */
  googleRedirectError: string | null;
  logout: () => Promise<void>;
}

export const PortalAuthContext = createContext<PortalAuthContextType | undefined>(undefined);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<PortalUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [googleRedirectError, setGoogleRedirectError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }
      try {
        const { claims } = await firebaseUser.getIdTokenResult();
        // Só reconhecemos como paciente se o Firebase tiver setado a claim.
        // Staff logado no sistema interno não deve aparecer como "logado"
        // aqui — fica null e o /portal/painel redireciona pra /portal/login.
        if (claims.role !== 'PACIENTE') {
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }
        setUser(firebaseUser);
        setProfile({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          clinicId: (claims.clinicId as string) || 'default-clinic',
        });
      } catch (err) {
        console.error('[portal-auth] token claims fetch failed:', err);
        setUser(null);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Login client-side puro. Os custom claims já foram setados pelo backend
  // durante o signup e viajam no ID token automaticamente.
  const loginWithPassword = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  /**
   * "Adota" o usuário recém-saído do Google como paciente. Diferente do
   * email+senha, o usuário Firebase nasce no CLIENT sem claim nenhuma —
   * chamamos `/auth/social-init` pro backend setar `role: PACIENTE` +
   * `clinicId` e forçamos o refresh do token pra claim valer. O
   * `onAuthStateChanged` acima dispara antes disso e ainda vê o token SEM
   * claim (seta user=null); por isso setamos o estado manualmente no fim.
   *
   * Se a conta Google for de staff, o backend responde 403 STAFF_ACCOUNT:
   * deslogamos e propagamos um erro amigável pra página mostrar.
   */
  const adoptGoogleUser = async (googleUser: User, clinicId: string) => {
    let tokenResult = await googleUser.getIdTokenResult();
    if (tokenResult.claims.role !== 'PACIENTE') {
      try {
        await PortalAPI.socialInit({ clinicId });
      } catch (err) {
        await signOut(auth);
        if (err instanceof PortalApiError && err.code === 'STAFF_ACCOUNT') {
          throw new Error('Esta conta Google é da equipe da clínica. Use o acesso interno do sistema.');
        }
        throw err;
      }
      // Claims setadas server-side só entram no token com refresh forçado.
      tokenResult = await googleUser.getIdTokenResult(true);
    }

    if (tokenResult.claims.role !== 'PACIENTE') {
      await signOut(auth);
      throw new Error('Não foi possível ativar sua conta de paciente. Tente novamente.');
    }

    setUser(googleUser);
    setProfile({
      uid: googleUser.uid,
      email: googleUser.email,
      displayName: googleUser.displayName,
      clinicId: (tokenResult.claims.clinicId as string) || clinicId,
    });
    setLoading(false);
  };

  /**
   * Login com Google via popup, com fallback pra redirect quando o popup é
   * bloqueado — comum em PWA instalada (standalone) e em webviews. No
   * fallback a página navega pro Google e volta; o retorno é tratado pelo
   * `getRedirectResult` no useEffect abaixo.
   */
  const loginWithGoogle = async (clinicId: string) => {
    const provider = new GoogleAuthProvider();
    // Sem isto o Google pula o seletor quando o navegador tem uma única
    // sessão e conecta direto nela — ruim em máquina compartilhada (família,
    // recepção) e pro próprio staff testando: cai sempre na conta errada sem
    // chance de escolher.
    provider.setCustomParameters({ prompt: 'select_account' });
    let cred;
    try {
      cred = await signInWithPopup(auth, provider);
    } catch (err) {
      if (err instanceof FirebaseError && err.code === 'auth/popup-blocked') {
        // Guarda a clínica pro retorno do redirect (a página remonta do zero).
        window.localStorage.setItem('portalGoogleClinicId', clinicId);
        await signInWithRedirect(auth, provider);
        return; // navega pro Google; não chega aqui
      }
      throw err;
    }
    await adoptGoogleUser(cred.user, clinicId);
  };

  // Retorno do fluxo redirect (fallback do popup bloqueado). Roda uma vez no
  // mount: se a página está voltando do Google, `getRedirectResult` traz o
  // usuário e fazemos a mesma adoção do fluxo popup. Erros não têm caller pra
  // receber o throw — vão pro `googleRedirectError`, que as páginas exibem.
  useEffect(() => {
    getRedirectResult(auth)
      .then(async (cred) => {
        if (!cred?.user) return; // carga normal, sem redirect pendente
        const clinicId = window.localStorage.getItem('portalGoogleClinicId') || 'default-clinic';
        window.localStorage.removeItem('portalGoogleClinicId');
        try {
          await adoptGoogleUser(cred.user, clinicId);
        } catch (err) {
          setGoogleRedirectError(
            err instanceof Error ? err.message : 'Não foi possível entrar com o Google. Tente novamente.',
          );
        }
      })
      .catch((err) => {
        console.error('[portal-auth] getRedirectResult falhou:', err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginWithCustomToken = async (token: string) => {
    await signInWithCustomToken(auth, token);
  };

  /**
   * Email de redefinição de senha mandado pelo PRÓPRIO Firebase Auth
   * (remetente noreply@dratento.firebaseapp.com, texto em Authentication →
   * Templates no console) — não depende do SMTP que segura o magic link.
   * O link abre a página de troca de senha do Firebase, e o "Continuar" de
   * lá volta pro /portal/login.
   *
   * Email sem conta resolve como sucesso, igual a quando tem: dizer "não
   * existe conta com esse email" viraria um oráculo de quem é paciente da
   * clínica (mesmo raciocínio do NO_MATCH no LinkIdentityCard). Com a
   * proteção contra enumeração do projeto ligada, o Firebase já se comporta
   * assim; o catch cobre o caso de ela estar desligada.
   */
  const requestPasswordReset = async (email: string) => {
    auth.languageCode = 'pt-BR';
    try {
      await sendPasswordResetEmail(auth, email, { url: `${window.location.origin}/portal/login` });
    } catch (err) {
      if (!(err instanceof FirebaseError)) throw err;
      if (err.code === 'auth/user-not-found') return;
      // Origem fora dos domínios autorizados (preview, IP local): manda sem o
      // botão "Continuar" em vez de não mandar.
      if (err.code === 'auth/unauthorized-continue-uri' || err.code === 'auth/invalid-continue-uri') {
        await sendPasswordResetEmail(auth, email);
        return;
      }
      throw err;
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  const isPortalAuthenticated = !!user && !!profile;

  return (
    <PortalAuthContext.Provider
      value={{ user, profile, loading, isPortalAuthenticated, loginWithPassword, loginWithGoogle, loginWithCustomToken, requestPasswordReset, googleRedirectError, logout }}
    >
      {children}
    </PortalAuthContext.Provider>
  );
}
